import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ShoppingCart, Menu, X, Globe, ChevronDown, User, Sun, Moon, Search, ChevronRight } from 'lucide-react';
import { useCurrency, CURRENCIES } from '../../contexts/CurrencyContext';
import { useCart } from '../../contexts/CartContext';
import { useStoreSettings, getLocalizedText } from '../../contexts/StoreSettingsContext';
import { useTheme } from '../../contexts/ThemeContext';
import { LANGUAGES } from '../../i18n';
import { SearchAutocomplete } from '../search/SearchAutocomplete';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Button } from '../ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Icônes pour les réseaux sociaux
function SocialIcon({ platform }) {
  switch (platform) {
    case 'instagram':
      return (
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
        </svg>
      );
    case 'tiktok':
      return (
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
          <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
        </svg>
      );
    case 'facebook':
      return (
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
        </svg>
      );
    default:
      return <Globe className="w-4 h-4" />;
  }
}

export function Header() {
  const { t, i18n } = useTranslation();
  const { currency, setCurrency } = useCurrency();
  const { cart } = useCart();
  const { settings } = useStoreSettings();
  const { theme, toggleTheme, isDark } = useTheme();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [categoriesOpen, setCategoriesOpen] = useState(false);

  const lang = i18n.language;
  
  // Paramètres du store
  const { brand, promo_banner, header } = settings;
  const storeName = getLocalizedText(brand?.store_name, lang);
  const bannerText = getLocalizedText(promo_banner?.text, lang);
  const navLinks = header?.nav_links?.filter(l => l.enabled) || [];
  const socialLinks = header?.social_links?.filter(s => s.enabled && s.url) || [];
  
  // Couleurs du header principal
  const headerBgColor = header?.background_color || (isDark ? '#0a0a0a' : '#ffffff');
  const headerTextColor = header?.text_color || (isDark ? '#ffffff' : '#0a0a0a');
  
  // Couleurs de la barre de navigation
  const navBgColor = header?.nav_background_color || headerBgColor;
  const navTextColor = header?.nav_text_color || headerTextColor;
  
  // Ordre des sections (configurable via admin)
  const sectionsOrder = header?.sections_order || ['promo_banner', 'main_header', 'nav_bar'];

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };

  // Charger les catégories pour le menu
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await fetch(`${API_URL}/api/categories`);
        if (res.ok) {
          const data = await res.json();
          setCategories(data.categories || data || []);
        }
      } catch (err) {
        console.error('Error fetching categories:', err);
      }
    };
    fetchCategories();
  }, []);

  // Helper pour obtenir le nom dans la bonne langue
  const getCategoryName = (name) => {
    if (!name) return '';
    if (typeof name === 'string') return name;
    return name[lang] || name.fr || name.en || '';
  };

  // Vérifier si on est sur une page de catégorie
  const isOnCategoryPage = location.pathname.startsWith('/category') || location.pathname.startsWith('/categories');
  const currentCategorySlug = location.pathname.split('/').pop();

  // Rendu du logo
  const renderLogo = () => {
    if (brand?.logo_dark) {
      const logoSrc = brand.logo_dark.startsWith('/api/') 
        ? `${API_URL}${brand.logo_dark}`
        : brand.logo_dark;
      return (
        <img 
          src={logoSrc} 
          alt={storeName}
          className="h-8 md:h-10 object-contain"
          data-testid="logo-image"
        />
      );
    }
    return (
      <img 
        src="/logo.png" 
        alt="Mutagim.shop"
        className="h-8 md:h-10 object-contain"
        data-testid="logo-image"
      />
    );
  };

  // ============================================================
  // SECTION: Bannière Promotionnelle
  // ============================================================
  const PromoBannerSection = () => {
    if (!promo_banner?.enabled) return null;
    return (
      <div 
        className="text-center text-sm py-1.5 px-4"
        style={{
          backgroundColor: promo_banner.background_color || '#C0000C',
          color: promo_banner.text_color || '#FFFFFF'
        }}
        data-testid="promo-banner"
      >
        {promo_banner.link_url ? (
          <a 
            href={promo_banner.link_url}
            target={promo_banner.link_target || '_self'}
            className="font-medium hover:underline"
          >
            {bannerText}
          </a>
        ) : (
          <span className="font-medium">{bannerText}</span>
        )}
      </div>
    );
  };

  // ============================================================
  // SECTION: Header Principal (Logo, Recherche, Actions)
  // ============================================================
  const MainHeaderSection = () => (
    <div 
      className="py-3"
      style={{ backgroundColor: headerBgColor }}
      data-testid="main-header"
    >
      <div 
        className="max-w-7xl mx-auto px-4 flex items-center justify-between gap-4"
        style={{ color: headerTextColor }}
      >
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="logo-link">
          {renderLogo()}
        </Link>

        {/* Search - Desktop */}
        <div className="hidden md:flex flex-1 max-w-xl">
          <SearchAutocomplete />
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1 md:gap-2">
          {/* Mobile search toggle */}
          <Button 
            variant="ghost" 
            size="icon"
            className="md:hidden hover:text-mutagim-red"
            style={{ color: 'inherit' }}
            onClick={() => setSearchOpen(!searchOpen)}
            data-testid="mobile-search-toggle"
          >
            <Search className="w-5 h-5" />
          </Button>

          {/* Theme Toggle */}
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleTheme}
            className="hover:text-mutagim-red"
            style={{ color: 'inherit' }}
          >
            {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </Button>

          {/* Language Selector - Desktop */}
          {header?.show_language_switch !== false && (
            <div className="hidden md:block">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="hover:text-mutagim-red gap-1"
                    style={{ color: 'inherit' }}
                    data-testid="language-switcher"
                  >
                    <Globe className="w-4 h-4" />
                    <span>{lang.toUpperCase()}</span>
                    <ChevronDown className="w-3 h-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-zinc-900 border-zinc-800">
                  {Object.entries(LANGUAGES).map(([code, langData]) => (
                    <DropdownMenuItem 
                      key={code}
                      onClick={() => changeLanguage(code)}
                      className={`cursor-pointer text-white hover:bg-gray-800 ${lang === code ? 'bg-gray-800' : ''}`}
                    >
                      {langData.flag} {langData.name}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}

          {/* Currency Selector - Desktop */}
          {header?.show_currency_switch !== false && (
            <div className="hidden md:block">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="hover:text-mutagim-red gap-1"
                    style={{ color: 'inherit' }}
                    data-testid="currency-switcher"
                  >
                    <span>{CURRENCIES[currency]?.symbol}</span>
                    <span>{currency}</span>
                    <ChevronDown className="w-3 h-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-zinc-900 border-zinc-800">
                  {Object.entries(CURRENCIES).map(([code, { symbol, name }]) => (
                    <DropdownMenuItem 
                      key={code}
                      onClick={() => setCurrency(code)}
                      className={`cursor-pointer text-white hover:bg-gray-800 ${currency === code ? 'bg-gray-800' : ''}`}
                    >
                      {symbol} {name}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}

          {/* Cart */}
          <Link to="/cart" data-testid="cart-link">
            <Button 
              variant="ghost" 
              size="icon" 
              className="relative hover:text-mutagim-red"
              style={{ color: 'inherit' }}
            >
              <ShoppingCart className="w-5 h-5" />
              {(cart.items?.length > 0 || cart.items_count > 0) && (
                <span className="absolute -top-1 -right-1 bg-mutagim-red text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                  {cart.items_count || cart.items?.reduce((sum, item) => sum + item.quantity, 0) || 0}
                </span>
              )}
            </Button>
          </Link>

          {/* Mobile menu toggle */}
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden hover:text-mutagim-red"
            style={{ color: 'inherit' }}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </Button>
        </div>
      </div>

      {/* Mobile Search - Expandable */}
      {searchOpen && (
        <div className="md:hidden px-4 pb-3">
          <SearchAutocomplete />
        </div>
      )}
    </div>
  );

  // ============================================================
  // SECTION: Barre de Navigation
  // ============================================================
  const NavBarSection = () => (
    <nav 
      className="hidden md:block border-t relative z-[60]"
      style={{ 
        backgroundColor: navBgColor,
        borderColor: isDark ? '#27272a' : '#e5e7eb'
      }}
      data-testid="nav-bar"
    >
      <div className="max-w-7xl mx-auto px-4">
        <ul className="flex items-center gap-8 h-12">
          {navLinks.map(link => {
            // Special handling for Categories link - show dropdown
            if (link.url === '/categories' || link.url === '/category') {
              return (
                <li key={link.id} className="relative group">
                  <button 
                    className="flex items-center gap-1 hover:text-mutagim-red transition-colors text-sm font-medium"
                    style={{ color: isOnCategoryPage ? '#dc2626' : navTextColor }}
                    data-testid={`nav-${link.id}`}
                    onMouseEnter={() => setCategoriesOpen(true)}
                    onClick={() => setCategoriesOpen(!categoriesOpen)}
                  >
                    {getLocalizedText(link.label, lang)}
                    <ChevronDown className={`w-3 h-3 transition-transform ${categoriesOpen ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {/* Categories Dropdown */}
                  <div 
                    className={`absolute top-full left-0 mt-1 py-2 min-w-[220px] rounded-lg shadow-xl border transition-all duration-200 z-[100] ${
                      categoriesOpen ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2'
                    }`}
                    style={{ 
                      backgroundColor: isDark ? '#18181b' : '#ffffff',
                      borderColor: isDark ? '#27272a' : '#e5e7eb'
                    }}
                    onMouseEnter={() => setCategoriesOpen(true)}
                    onMouseLeave={() => setCategoriesOpen(false)}
                  >
                    {/* Link to all categories */}
                    <Link
                      to="/categories"
                      className={`flex items-center gap-3 px-4 py-2 text-sm transition-colors ${
                        isDark ? 'hover:bg-zinc-800' : 'hover:bg-gray-100'
                      }`}
                      style={{ color: location.pathname === '/categories' ? '#dc2626' : (isDark ? '#ffffff' : '#0a0a0a') }}
                      data-testid="nav-all-categories"
                    >
                      <span className="text-base">📦</span>
                      <span className="font-medium">{t('nav.allCategories', 'Toutes les catégories')}</span>
                    </Link>
                    
                    <div className="my-2 border-t" style={{ borderColor: isDark ? '#27272a' : '#e5e7eb' }} />
                    
                    {/* Individual categories */}
                    {categories.filter(c => c.is_active !== false).slice(0, 8).map(category => (
                      <Link
                        key={category.id || category._id || category.slug}
                        to={`/category/${category.slug}`}
                        className={`flex items-center gap-3 px-4 py-2 text-sm transition-colors ${
                          isDark ? 'hover:bg-zinc-800' : 'hover:bg-gray-100'
                        }`}
                        style={{ 
                          color: currentCategorySlug === category.slug ? '#dc2626' : (isDark ? '#ffffff' : '#0a0a0a')
                        }}
                        data-testid={`nav-category-${category.slug}`}
                      >
                        <span className="text-base">{category.emoji || '📁'}</span>
                        <span>{getCategoryName(category.name)}</span>
                        {currentCategorySlug === category.slug && (
                          <ChevronRight className="w-3 h-3 ml-auto text-mutagim-red" />
                        )}
                      </Link>
                    ))}
                    
                    {categories.length > 8 && (
                      <>
                        <div className="my-2 border-t" style={{ borderColor: isDark ? '#27272a' : '#e5e7eb' }} />
                        <Link
                          to="/categories"
                          className={`flex items-center gap-3 px-4 py-2 text-sm transition-colors ${
                            isDark ? 'hover:bg-zinc-800 text-gray-400' : 'hover:bg-gray-100 text-gray-500'
                          }`}
                        >
                          <span>+{categories.length - 8} autres</span>
                          <ChevronRight className="w-3 h-3 ml-auto" />
                        </Link>
                      </>
                    )}
                  </div>
                </li>
              );
            }
            
            // Regular nav link
            return (
              <li key={link.id}>
                <Link 
                  to={link.url}
                  target={link.target || '_self'}
                  className="hover:text-mutagim-red transition-colors text-sm font-medium"
                  style={{ color: navTextColor }}
                  data-testid={`nav-${link.id}`}
                >
                  {getLocalizedText(link.label, lang)}
                </Link>
              </li>
            );
          })}
        </ul>
      </div>
    </nav>
  );

  // ============================================================
  // RENDU DES SECTIONS DANS L'ORDRE CONFIGURÉ
  // ============================================================
  const renderSection = (sectionId) => {
    switch (sectionId) {
      case 'promo_banner':
        return <PromoBannerSection key="promo_banner" />;
      case 'main_header':
        return <MainHeaderSection key="main_header" />;
      case 'nav_bar':
        return <NavBarSection key="nav_bar" />;
      default:
        return null;
    }
  };

  return (
    <header 
      className="sticky top-0 z-50 border-b transition-colors"
      style={{ borderColor: isDark ? '#27272a' : '#e5e7eb' }}
      data-testid="header"
    >
      {/* Rendu dynamique des sections dans l'ordre configuré */}
      {sectionsOrder.map(sectionId => renderSection(sectionId))}

      {/* Mobile menu - Toujours à la fin */}
      {mobileMenuOpen && (
        <div 
          className="md:hidden border-t"
          style={{ 
            backgroundColor: navBgColor,
            borderColor: isDark ? '#27272a' : '#e5e7eb'
          }}
        >
          <nav className="px-4 py-4 space-y-4">
            {navLinks.map(link => (
              <Link 
                key={link.id}
                to={link.url}
                className="block hover:text-mutagim-red text-lg"
                style={{ color: navTextColor }}
                onClick={() => setMobileMenuOpen(false)}
              >
                {getLocalizedText(link.label, lang)}
              </Link>
            ))}
            <Link 
              to="/account" 
              className="block hover:text-mutagim-red text-lg"
              style={{ color: navTextColor }}
              onClick={() => setMobileMenuOpen(false)}
            >
              {t('nav.account')}
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}

export default Header;
